// Wear history for a single item — reached from the item detail "Last Worn" row.

import { useParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Badge, LoadingState, ErrorState, EmptyState } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { formatDate } from '@/lib/utils';

interface ItemWear {
  id: string;
  wornDate: string;
  occasion: string | null;
  locationText: string | null;
  notes: string | null;
}

interface WearsResponse {
  itemName: string;
  wears: ItemWear[];
}

export function ItemWearsPage() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error } = useApi<WearsResponse>(id ? `/items/${id}/wears` : null);

  return (
    <Page>
      <PageHeader section="Wear History" backHref={`/wardrobe/item/${id}`} backLabel="Item" />
      <Main className="space-y-4">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && data && (
          <>
            <div>
              <h1 className="text-lg font-bold text-foreground leading-tight">{data.itemName}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {data.wears.length} wear{data.wears.length === 1 ? '' : 's'}
              </p>
            </div>

            {data.wears.length === 0 ? (
              <EmptyState
                icon={<Star className="w-8 h-8" />}
                title="No wears logged yet"
                description="Log a wear from the item's page to start tracking it here."
              />
            ) : (
              <div className="space-y-2">
                {data.wears.map((w) => (
                  <Card key={w.id} className="p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-foreground">
                        {formatDate(w.wornDate)}
                      </span>
                      {w.occasion && <Badge tone="neutral">{w.occasion}</Badge>}
                    </div>
                    {(w.locationText || w.notes) && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {[w.locationText, w.notes].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </Main>
    </Page>
  );
}
